import { Button } from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useReturnRequest } from "@babackapp/return-widget-components/queries/use-return-requests";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { isCancellable } from "../../utils/is-cancellable";
import { getCarrierRelayUrl } from "../../utils/carrier-relay-url";
import { resolveReturnLocation } from "../../utils/resolve-return-method";
import { ReturnContent } from "./return-content";
import { ReturnStatusTimeline } from "./return-status-timeline";
import { StatusAlert } from "./status-alert";
import { ReturnMethodSection } from "./return-method-section";
import { CancelReturnSection } from "./cancel-return-section";

export function ReturnRequestDetails() {
  const { orderId, shopId, shopSlug, returnRequestId, logoUrl } = useWidgetParams();
  const t = useCustomText();
  const { data: returnRequest } = useReturnRequest({
    orderId,
    shopSlug,
    returnRequestId: returnRequestId!,
  });
  const { data: shop } = usePublicShop(shopId);
  const { data: order } = usePublicOrder(orderId);

  const canCancel =
    shop.preferences.canCancelReturnRequest &&
    isCancellable(returnRequest.status);

  const returnStore = shop.returnToStore.stores.find(
    (s) => s.id === returnRequest.shipping_info.returnStoreId,
  );

  const returnLocation = resolveReturnLocation(
    shop.locations,
    returnRequest.shipping_info.locationId,
  );

  const shippingAddress = (order as { shippingAddress?: unknown })?.shippingAddress ?? null;
  const isStoreReturn = !!returnRequest.shipping_info.returnStoreId;
  const relayUrl =
    !isStoreReturn && shippingAddress
      ? getCarrierRelayUrl(returnRequest.shipping_info.carrier, shippingAddress as Parameters<typeof getCarrierRelayUrl>[1])
      : null;

  return (
    <WidgetPage className="pt-10 pb-8">
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>
          <Link to="..">
            <Button variant="ghost">
              <ArrowLeft />
            </Button>
          </Link>
          {t("returnRequests.details.title")}
        </HeaderTitle>
        <HeaderDescription>
          {t("returnRequests.details.description")}
        </HeaderDescription>
      </Header>

      <ReturnContent returnRequest={returnRequest} />
      <StatusAlert status={returnRequest.status} shippingInfo={returnRequest.shipping_info} relayUrl={relayUrl} />
      <ReturnStatusTimeline status={returnRequest.status} />
      <ReturnMethodSection
        shippingInfo={returnRequest.shipping_info}
        returnStore={returnStore}
        location={returnLocation}
      />
      {canCancel && <CancelReturnSection returnRequestId={returnRequest.id} />}
    </WidgetPage>
  );
}
